import {
  Component,
  ChangeDetectionStrategy,
  signal,
  input,
  output,
  EventEmitter,
  computed,
  InputSignal,
  Input,
  model,
  Output,
  ViewChild,
} from '@angular/core';
import { ChipsItem } from '../../types/chips';
import { UChipsModule } from '../u-chips/u-chips.module';
import { UInputModule } from '../u-input/u-input.module';
import { UInputComponent } from '../u-input/u-input.component';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { UBaseInfraComponent } from '../u-base-infra.component';
import { UButtonModule } from '../u-button/u-button.module';
import { UIconModule } from '../u-icon/u-icon.module';
import { MatChipsModule } from '@angular/material/chips';
@Component({
  selector: 'u-input-chips',
  templateUrl: './u-input-chips.component.html',
  styleUrls: ['./u-input-chips.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UChipsModule, UInputModule, NgTemplateOutlet, NgClass, UButtonModule, UIconModule, MatChipsModule],
})
export class UInputChipsComponent extends UBaseInfraComponent {
  items = model<ChipsItem[]>([]);

  @ViewChild('chipInput') chipInput: UInputComponent;

  readonly inputValue = signal('');
  readonly placeholder = input<string>('Add item');
  readonly disabled = input<boolean>(false);
  readonly validationRegex = input<RegExp | null>(null);
  readonly maxTags = input<number | null>(null);
  readonly allowDuplicate = input<boolean>(false);
  readonly showShortUrl = input<boolean>(false);
  readonly errorMessage = input<string>('');
  readonly style = input<any>({ width: '500px', maxHeight: '200px' });
  readonly showClear = input<boolean>(false);
  itemsChanged = output<ChipsItem[]>();
  onMaxTagsError = output<void>();
  @Output() inputValueChanged = new EventEmitter<string>();
  @Output() inputFocus = new EventEmitter<boolean>();
  @Output() inputBlur = new EventEmitter<boolean>();
  readonly isInputFocused = signal<boolean>(false);

  setInputValue(val: string) {
    this.inputValue.set(val);
    this.inputValueChanged.emit(val);
    const values = this.inputValue()
      ?.split(',')
      ?.map((v) => v.trim())
      ?.filter((v) => v)
      .filter((v) => (this.validationRegex() ? this.validationRegex()!.test(v) : true));

    if (values?.length) {
      this.itemsChanged.emit([...this.items(), ...values.map((value) => ({ value, isValid: true }))]);
    } else {
      this.itemsChanged.emit(this.items());
    }
  }

  addItem(val: string) {
    const value = (val || '').trim();
    if (!value.trim()) return;

    const isValid = this.validationRegex() ? this.validationRegex()!.test(value) : true;

    const currentItems = this.items();

    if (!this.allowDuplicate() && currentItems.some((i) => i.value === value)) {
      this.inputValue.set('');
      return;
    }
    if (!this.maxTags() || currentItems.length < this.maxTags()) {
      const newItems = [...currentItems, { value, isValid }];
      this.items.set(newItems);
      this.itemsChanged.emit(newItems);
      this.setInputValue('');
    } else {
      this.onMaxTagsError.emit();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const separator = ',';
    const pastedData = (event.clipboardData || (window as any)['clipboardData'])?.getData('Text');
    if (!pastedData) return;

    let tags = pastedData
      .split(separator)
      .map((v) => v.trim())
      .filter((v) => v);

    const items = this.items() ?? [];
    const updatedItems = [...items];
    tags.forEach((val) => {
      if (updatedItems.some((i) => i.value === val)) return;
      const isValid = this.validationRegex() ? this.validationRegex()!.test(val) : true;
      updatedItems.push({ value: val, isValid });
    });

    this.items.set(updatedItems);
    this.itemsChanged.emit(updatedItems);
    this.setInputValue('');
  }
  onInputFocus() {
    this.isInputFocused.set(true);
    this.inputFocus.emit(true);
  }
  onInputBlur() {
    this.isInputFocused.set(false);
    this.inputBlur.emit(false);
  }

  clearAll() {
    this.items.set([]);
    this.itemsChanged.emit([]);
    this.setInputValue('');
    this.chipInput?.focusInput();
  }

  getIconRef() {
    return null;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.inputValue().trim()) {
      const currentItems = this.items() ?? [];
      if (currentItems.length > 0) {
        const updated = currentItems.slice(0, -1);
        this.items.set(updated);
        this.itemsChanged.emit(updated);
        event.preventDefault();
      }
    }
    if(event.key === ',') {
      event.preventDefault();
      this.addItem(this.inputValue());
      this.setInputValue('');
    }
  }

  isValid(): boolean {
    const items = this.items();
    return !items?.some((i) => !i.isValid);
  }

  isValidAll(): boolean {
    const items = this.items();
    return !!items?.length && this.isValid();
  }

  onItemsChanged(updatedItems: ChipsItem[]) {
    this.items.set(updatedItems);
    this.itemsChanged.emit(updatedItems);
    this.chipInput?.focusInput();
  }
}
